import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Upload, Loader2, CheckCircle2, Coins, CreditCard, ExternalLink, UserPlus, FileSpreadsheet } from "lucide-react";
import { api, errorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { SUPPORTED } from "@/i18n";
import { tDynamic } from "@/i18n/dynamic";
import { fmtDateTime, fmtShortDate, fmtMoney, fmtNumber } from "@/i18n/format";
import { PageHeader, EmptyState, Pill, STATUS_TONE } from "@/components/common";
import { STATUS_LABEL } from "@/pages/app/AIDiscovery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { SecuritySettings } from "@/components/SecuritySettings";
import { PackGrid, useCatalog } from "@/components/CreditPacks";

function CreditBreakdown({ w }) {
  const { t } = useI18n();
  const b = w.buckets || {};
  const cells = [["wallet-balance", t("Available credits"), w.balance], ["wallet-subscription", t("Included (subscription / trial)"), (b.subscription || 0) + (b.trial || 0)],
    ["wallet-purchased", t("Purchased credits"), b.purchased || 0], ["wallet-reserved", t("Reserved by running jobs"), w.reserved]];
  return (
    <div className="grid sm:grid-cols-4 gap-3 mb-4">{cells.map(([id, label, v]) => <div key={id} className="card-surface p-5"><div className="text-3xl font-bold font-mono" data-testid={id}>{fmtNumber(v)}</div><div className="text-xs text-muted-foreground mt-1">{label}</div></div>)}
      <div className="sm:col-span-4 text-xs text-muted-foreground flex flex-wrap gap-4" data-testid="wallet-periods">
        {w.allocation_ends_at && <span>{t("Current allocation ends on {{date}}", { date: fmtShortDate(w.allocation_ends_at) })}</span>}
        {w.next_renewal && <span>{t("Next credit renewal: {{date}}", { date: fmtShortDate(w.next_renewal) })}</span>}
        <span>{t("Expired: {{count}}", { count: b.expired || 0 })}</span><span>{t("Refunded: {{count}}", { count: b.refunded || 0 })}</span></div>
    </div>
  );
}

function UsageByFeature({ rows }) {
  const { t } = useI18n();
  if (!rows?.length) return null;
  const max = Math.max(...rows.map((r) => r.credits), 1);
  return <div className="card-surface p-5 mb-6" data-testid="usage-by-feature"><div className="font-semibold text-sm mb-3">{t("Credits consumed by feature")}</div>
    {rows.map((r) => <div key={r.feature} className="flex items-center gap-3 text-xs mb-2"><span className="w-40 truncate text-muted-foreground">{lbl(t, r.feature)}</span>
      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full ai-gradient" style={{ width: `${(100 * r.credits) / max}%` }} /></div><b className="font-mono w-16 text-right">{fmtNumber(r.credits)}</b></div>)}</div>;
}

const lbl = (t, s) => t(String(s || "").replaceAll("_", " "));
const ROLES = ["owner", "admin", "recruiter", "viewer"];

export function Imports() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [rights, setRights] = useState("customer_provided");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const ref = useRef();
  const load = () => api.get("/imports").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!rows.some((r) => ["queued", "processing"].includes(r.status))) return;
    const h = setTimeout(load, 2500);
    return () => clearTimeout(h);
  }, [rows]);
  const upload = async (f) => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", f); fd.append("rights_basis", rights);
    try { await api.post("/imports", fd); toast.success(t("Import queued")); load(); } catch (e) { toast.error(errorDetail(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  const cols = [["File"], ["Status"], ["Rows"], ["Created"], ["Merged"], ["Skipped"], ["Errors"]];
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader eyebrow={t("Private talent pool")} title={t("Imports")} desc={t("Upload CSV/XLSX files into your organization's private pool. Rows are validated, normalized, skill-mapped, deduplicated and checked against suppression records in a background job.")} />
      <div className="card-surface p-5 mb-6 flex flex-col sm:flex-row gap-4 sm:items-end">
        <label className="text-xs font-medium flex-1">{t("Legal basis / data rights")}
          <select value={rights} onChange={(e) => setRights(e.target.value)} className="mt-1 w-full h-9 rounded-md border bg-background px-2 text-sm" data-testid="import-rights-select">
            <option value="customer_provided">{t("Our own candidate records (customer provided)")}</option>
            <option value="applicant_submitted">{t("Applications submitted to us")}</option>
            <option value="candidate_consented">{t("Candidates consented to processing")}</option>
          </select></label>
        <Button onClick={() => ref.current.click()} disabled={busy} data-testid="import-upload-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{t("Upload CSV / XLSX")}</Button>
        <input ref={ref} type="file" accept=".csv,.xlsx" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="import-file-input" />
      </div>
      <p className="text-xs text-muted-foreground mb-4">{t("Columns:")} <span className="font-mono" translate="no">{/* i18n-ignore */}full_name, email, phone, linkedin_url, current_title, current_company, country, city, years_experience, skills (;), languages (;), industries (;), headline, summary, education (institution|degree)</span></p>
      {!rows.length ? <EmptyState icon={FileSpreadsheet} title={t("No imports yet")} /> : (
        <Table data-testid="imports-table"><TableHeader><TableRow>{cols.map(([c]) => <TableHead key={c}>{t(c)}</TableHead>)}</TableRow></TableHeader>
          <TableBody>{rows.map((r) => (
            <TableRow key={r.id} className="cursor-pointer" onClick={() => api.get(`/imports/${r.id}`).then((x) => setDetail(x.data))} data-testid={`import-row-${r.id}`}>
              <TableCell className="font-medium">{r.filename}</TableCell><TableCell><Pill tone={STATUS_TONE[r.status]}>{t(STATUS_LABEL[r.status] || r.status)}</Pill></TableCell>
              <TableCell>{fmtNumber(r.total_rows)}</TableCell><TableCell>{fmtNumber(r.created_rows)}</TableCell><TableCell>{fmtNumber(r.merged_rows)}</TableCell><TableCell>{fmtNumber(r.skipped_rows)}</TableCell><TableCell className={r.error_rows ? "text-red-600" : ""}>{fmtNumber(r.error_rows)}</TableCell>
            </TableRow>))}</TableBody></Table>
      )}
      {detail && (
        <div className="card-surface p-4 mt-4" data-testid="import-errors">
          <div className="font-semibold text-sm mb-2">{t("{{file}} — row errors ({{count}})", { file: detail.filename, count: detail.errors.length })}</div>
          {detail.errors.map((e) => <div key={e.row} className="text-xs font-mono py-0.5">{t("row {{row}}: {{error}}", { row: e.row, error: tDynamic(e.error) })}</div>)}
          {!detail.errors.length && <p className="text-xs text-muted-foreground">{t("No row errors.")}</p>}
        </div>
      )}
    </div>
  );
}

export function Credits() {
  const { t } = useI18n();
  const [w, setW] = useState(null);
  const [tx, setTx] = useState({ items: [] });
  const [catalog] = useCatalog();
  const { user } = useAuth();
  useEffect(() => { api.get("/credits/wallet").then((r) => setW(r.data)); api.get("/credits/transactions").then((r) => setTx(r.data)); }, []);
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader eyebrow={t("AI Credits")} title={t("AI Credits & Usage")} desc={t("Credits are reserved before each AI operation and reconciled on completion; failed or cancelled jobs are refunded. Included credits are used first, then purchased credits.")} />
      {w && <CreditBreakdown w={w} />}
      {w && <UsageByFeature rows={w.by_feature} />}
      <div className="font-semibold text-sm mb-2">{t("Credit packages")}</div>
      {user?.entitlements?.is_paid ? <PackGrid catalog={catalog} /> : <p className="text-sm text-muted-foreground mb-2" data-testid="packs-need-plan">{t("Credit packages are available with an active paid subscription.")}</p>}
      <div className="h-6" />
      {w && (
        <div className="card-surface p-5 text-xs space-y-1 mb-6 max-w-md" data-testid="credit-tariffs"><div className="font-semibold text-sm mb-2">{t("Cost per operation")}</div>{Object.entries(w.tariffs).filter(([, v]) => v > 0).map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-muted-foreground">{lbl(t, k)}</span><b className="font-mono">{v}</b></div>)}</div>
      )}
      <Table data-testid="credit-ledger"><TableHeader><TableRow><TableHead>{t("Date")}</TableHead><TableHead>{t("Type")}</TableHead><TableHead>{t("Reason")}</TableHead><TableHead className="text-right">{t("Amount")}</TableHead><TableHead className="text-right">{t("Balance")}</TableHead></TableRow></TableHeader>
        <TableBody>{tx.items.map((x) => (
          <TableRow key={x.id}><TableCell className="text-xs">{fmtDateTime(x.created_at)}</TableCell><TableCell><Pill>{lbl(t, x.type)}</Pill></TableCell><TableCell className="text-sm">{tDynamic(x.reason)}</TableCell>
            <TableCell className={`text-right font-mono ${x.amount > 0 ? "text-emerald-600" : x.amount < 0 ? "text-red-600" : ""}`}>{x.amount > 0 ? "+" : ""}{fmtNumber(x.amount)}</TableCell><TableCell className="text-right font-mono">{fmtNumber(x.balance_after)}</TableCell></TableRow>))}</TableBody></Table>
    </div>
  );
}

export function Billing() {
  const { user, setUpgradeOpen } = useAuth();
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [inv, setInv] = useState([]);
  const isAdmin = ["owner", "admin"].includes(user.memberships[0]?.role);
  useEffect(() => { api.get("/billing/subscription").then((r) => setS(r.data)); if (isAdmin) api.get("/billing/invoices").then((r) => setInv(r.data)).catch(() => {}); }, [isAdmin]);
  const portal = async () => { try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; } catch (e) { toast.error(errorDetail(e)); } };
  const e = s?.entitlements;
  const [catalog, reloadCatalog] = useCatalog();
  const setCurrency = async (c) => { try { await api.put("/billing/currency", { currency: c }); reloadCatalog(); } catch (err) { toast.error(errorDetail(err)); } };
  const plan = { annual: t("Annual plan"), monthly: t("Monthly plan"), trial: t("Free trial") }[e?.plan] || lbl(t, e?.plan);
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageHeader eyebrow={t("Subscription")} title={t("Billing")} desc={t("Secure payments by Stripe. Paid features are activated only after Stripe confirms the payment.")} />
      {e && (
        <div className="card-surface p-6 grid sm:grid-cols-4 gap-4 mb-6" data-testid="billing-summary">
          <div><div className="eyebrow">{t("Plan")}</div><div className="text-xl font-bold" data-testid="billing-plan">{plan}</div></div>
          <div><div className="eyebrow">{t("Status")}</div><Pill tone={STATUS_TONE[e.status]} testid="billing-status">{lbl(t, e.status)}</Pill></div>
          <div><div className="eyebrow">{t("Seats")}</div><div className="text-xl font-bold font-mono">{e.seats} <span className="text-xs text-muted-foreground">({t("{{count}} members", { count: s.seats_used })})</span></div></div>
          <div><div className="eyebrow">{e.is_paid ? t("Renews on") : t("Trial ends on")}</div><div className="text-sm font-medium">{fmtShortDate(e.is_paid ? e.current_period_end : e.trial_ends_at)}</div></div>
        </div>
      )}
      <div className="flex gap-2 mb-8 flex-wrap items-center">
        {catalog && <div className="inline-flex rounded-full border p-1 mr-2" data-testid="billing-currency">{["EUR", "USD", "CAD"].map((c) => <button key={c} disabled={catalog.currency_locked || !isAdmin} onClick={() => setCurrency(c)} data-testid={`billing-currency-${c}`}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors disabled:opacity-60 ${catalog.currency === c ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{c}</button>)}</div>}
        {!e?.is_paid && <Button onClick={() => setUpgradeOpen(true)} disabled={!isAdmin} data-testid="billing-upgrade-button"><CreditCard className="w-4 h-4" />{t("Choose a plan")}</Button>}
        {s?.stripe_customer && <Button variant="outline" onClick={portal} disabled={!isAdmin} data-testid="billing-portal-button">{t("Manage my subscription")}<ExternalLink className="w-4 h-4" /></Button>}
      </div>
      <div className="font-semibold text-sm mb-2">{t("Invoices")}</div>
      {!inv.length ? <p className="text-sm text-muted-foreground">{t("No invoices yet.")}</p> : (
        <Table><TableBody>{inv.map((i) => <TableRow key={i.id}><TableCell>{i.number}</TableCell><TableCell><Pill>{lbl(t, i.status)}</Pill></TableCell><TableCell className="font-mono">{fmtMoney(i.amount_paid, i.currency || "EUR", { maximumFractionDigits: 2 })}</TableCell><TableCell>{fmtShortDate(i.created * 1000)}</TableCell><TableCell>{i.hosted_invoice_url && <a href={i.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-primary text-xs">{t("View")}</a>}</TableCell></TableRow>)}</TableBody></Table>
      )}
    </div>
  );
}

export function BillingSuccess() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("checking");
  useEffect(() => {
    let n = 0, stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/checkout/${params.get("session_id")}`);
        if (data.payment_status === "paid" && data.entitlements.is_paid) { setState("paid"); refresh(); return; }
      } catch { /* retry */ }
      if (++n < 15 && !stop) setTimeout(poll, 2000); else setState("pending");
    };
    poll();
    return () => { stop = true; };
  }, [params, refresh]);
  return (
    <div className="p-10 max-w-lg mx-auto text-center" data-testid="billing-success">
      {state === "checking" && <><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" /><p className="mt-4 text-sm">{t("Confirming your subscription with Stripe…")}</p></>}
      {state === "paid" && <><CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600" /><h1 className="text-2xl font-bold mt-4">{t("Subscription active")}</h1><p className="text-sm text-muted-foreground mt-2">{t("Paid entitlements and AI credits have been provisioned.")}</p><Button className="mt-6" onClick={() => nav("/app/search")} data-testid="billing-success-continue">{t("Continue to Talent Search")}</Button></>}
      {state === "pending" && <><h1 className="text-xl font-bold">{t("Payment processing")}</h1><p className="text-sm text-muted-foreground mt-2">{t("We have not yet received Stripe's confirmation. Entitlements activate automatically once it arrives.")}</p></>}
    </div>
  );
}

export function Team() {
  const { user } = useAuth();
  const { t } = useI18n();
  const org = user.memberships[0];
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("recruiter");
  const load = () => api.get(`/organizations/${org.organization_id}/members`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const add = async () => { try { await api.post(`/organizations/${org.organization_id}/members`, { email, role }); setEmail(""); load(); toast.success(t("Member added")); } catch (e) { toast.error(errorDetail(e)); } };
  const setR = async (m, r) => { try { await api.patch(`/organizations/${org.organization_id}/members/${m.id}`, { role: r }); load(); } catch (e) { toast.error(errorDetail(e)); } };
  const isAdmin = ["owner", "admin"].includes(org.role);
  const opts = ROLES.slice(1).map((r) => <option key={r} value={r}>{t(`role.${r}`)}</option>);
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageHeader eyebrow={org.organization_name} title={t("Team")} desc={t("Roles: Owner, Admin, Recruiter, Hiring Manager (read-only). Permissions are enforced by the API.")} />
      {isAdmin && (
        <div className="card-surface p-4 flex flex-col sm:flex-row gap-2 mb-6">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("colleague@company.com (must have a recruiter account)")} data-testid="member-email-input" />
          <select value={role} onChange={(e) => setRole(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-sm" data-testid="member-role-select">{opts}</select>
          <Button onClick={add} disabled={!email} data-testid="add-member-button"><UserPlus className="w-4 h-4" />{t("Add")}</Button>
        </div>
      )}
      <Table data-testid="members-table"><TableHeader><TableRow><TableHead>{t("Name")}</TableHead><TableHead>{t("Email")}</TableHead><TableHead>{t("Role")}</TableHead><TableHead>{t("Joined")}</TableHead></TableRow></TableHeader>
        <TableBody>{rows.map((m) => (
          <TableRow key={m.id}><TableCell className="font-medium">{m.full_name}</TableCell><TableCell>{m.email}</TableCell>
            <TableCell>{isAdmin && m.role !== "owner" ? <select value={m.role} onChange={(e) => setR(m, e.target.value)} className="h-8 rounded border bg-background px-1 text-xs" data-testid={`role-select-${m.id}`}>{opts}</select> : <Pill>{t(`role.${m.role}`)}</Pill>}</TableCell>
            <TableCell className="text-xs">{fmtShortDate(m.joined_at)}</TableCell></TableRow>))}</TableBody></Table>
    </div>
  );
}

export function SettingsPage() {
  const { user, refresh } = useAuth();
  const { t, locale, setLang } = useI18n();
  const [name, setName] = useState(user.full_name);
  const [org, setOrg] = useState(user.memberships[0]?.organization_name || "");
  const save = async () => {
    try {
      await api.patch("/users/me", { full_name: name });
      if (["owner", "admin"].includes(user.memberships[0]?.role)) await api.patch("/organizations/current", { name: org });
      await refresh(); toast.success(t("Saved"));
    } catch (e) { toast.error(errorDetail(e)); }
  };
  return (
    <div className="p-6 max-w-2xl mx-auto">
      <PageHeader title={t("Settings")} />
      <div className="card-surface p-5 space-y-4">
        <label className="block text-sm font-medium">{t("Your name")}<Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="settings-name-input" /></label>
        <label className="block text-sm font-medium">{t("Organization name")}<Input value={org} onChange={(e) => setOrg(e.target.value)} className="mt-1" data-testid="settings-org-input" /></label>
        <label className="block text-sm font-medium">{t("Interface language")}
          <select value={locale} onChange={(e) => setLang(e.target.value)} className="mt-1 w-full h-9 rounded-md border bg-background px-2 text-sm" data-testid="settings-language-select">
            {Object.entries(SUPPORTED).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select></label>
        <Button onClick={save} data-testid="settings-save-button">{t("Save")}</Button>
      </div>
      <div className="mt-4"><SecuritySettings /></div>
    </div>
  );
}
